import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Grid, Paper, Typography, Stack, Button, Chip, Divider,
  CircularProgress, List, ListItem, ListItemText, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Autocomplete, Breadcrumbs, Link,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LinkOff as LinkIcon,
  NoteAdd as NoteIcon,
  Visibility as PipelineIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import fundedAPI from '../../api/funded';
import FundingTable from '../../components/funded/FundingTable';
import { currency, fmtDate, fmtDateTime, clientIdLabel } from '../../components/funded/format';

const Stat = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" fontWeight={800} color={color}>{value}</Typography>
  </Box>
);

// Dialog: pick an unmatched deposit and attach it to THIS client.
function LinkDepositDialog({ open, clientId, onClose, onLinked }) {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setSelected(null); setInputValue(''); setOptions([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fundedAPI.getUnmatched({ search: inputValue, limit: 25 });
        if (active) setOptions(res.data.data || []);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [open, inputValue]);

  const submit = async () => {
    if (!selected) return toast.error('Select a deposit');
    setSaving(true);
    try {
      await fundedAPI.matchDeposit(selected.deposit_id, { client_id: clientId });
      toast.success('Deposit linked — funding & commission created');
      onLinked?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to link deposit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Unmatched Deposit</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={options}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          getOptionLabel={(o) => `${o.deposit_id} · ${currency(o.amount)} · ${o.lender || ''}`}
          isOptionEqualToValue={(o, v) => o.deposit_id === v.deposit_id}
          loading={loading}
          renderOption={(props, o) => (
            <li {...props} key={o.deposit_id}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{currency(o.amount)} · {o.lender || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary">{fmtDate(o.date)} · {o.deposit_id}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Search unmatched deposits" autoFocus />}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Linking…' : 'Link Deposit'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkOpen, setLinkOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fundedAPI.getClient(clientId);
      setData(res.data.data);
    } catch (e) {
      if (e.response?.status === 404) toast.error('Client not found');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await fundedAPI.addClientNote(clientId, { content: noteText.trim() });
      toast.success('Note added');
      setNoteText('');
      setNoteOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;
  if (!data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/sales/funded/clients')}>Back to clients</Button>
        <Typography sx={{ mt: 2 }}>Client not found.</Typography>
      </Container>
    );
  }

  const { client, fundings, commissions, applications, notes, pipelineMatch, totals } = data;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/sales/funded/clients')}>Clients</Link>
        <Typography color="text.primary">{client.business_name}</Typography>
      </Breadcrumbs>

      <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#2563eb15', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BusinessIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>{client.business_name}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">{clientIdLabel(client.client_id)}</Typography>
                <Chip label={client.status || 'funded'} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => setLinkOpen(true)}>Link unmatched deposit</Button>
            <Button size="small" variant="outlined" startIcon={<NoteIcon />} onClick={() => setNoteOpen(true)}>Add note</Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PipelineIcon />}
              disabled={!pipelineMatch}
              onClick={() => navigate('/sales')}
            >
              {pipelineMatch ? 'View in pipeline' : 'No pipeline match'}
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
          <Stat label="Total Commissions" value={currency(totals.totalCommissions)} color="success.main" />
          <Stat label="Total Funded" value={currency(totals.totalFundings)} />
          <Stat label="Fundings" value={totals.fundingCount} />
          <Stat label="Lenders" value={totals.lenders.length} />
          <Stat label="First Deposit" value={fundings.length ? fmtDate(fundings[0].date) : '—'} />
          <Stat label="Last Deposit" value={fundings.length ? fmtDate(fundings[fundings.length - 1].date) : '—'} />
        </Stack>
        {pipelineMatch && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Pipeline match: {pipelineMatch.businessName} ({pipelineMatch.status})
          </Typography>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Fundings ({fundings.length})</Typography>
          <FundingTable rows={fundings} emptyText="No fundings" />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Commissions ({commissions.length})</Typography>
          <FundingTable rows={commissions} amountLabel="Commission" emptyText="No commissions" />
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Attached Applications ({applications.length})</Typography>
            {applications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No attached Adobe applications.</Typography>
            ) : (
              <List dense disablePadding>
                {applications.map((a, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText
                      primary={<Typography variant="body2">{a.legal_name || a.email || a.ein || `Application ${i + 1}`}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{[a.owner_name, a.funding_amount != null ? currency(a.funding_amount) : null, a.source].filter(Boolean).join(' · ')}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Notes ({notes.length})</Typography>
              <Button size="small" startIcon={<NoteIcon />} onClick={() => setNoteOpen(true)}>Add</Button>
            </Stack>
            {notes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No notes yet.</Typography>
            ) : (
              <List dense disablePadding>
                {[...notes].reverse().map((n, i) => (
                  <ListItem key={n._id || i} disableGutters alignItems="flex-start">
                    <ListItemText
                      primary={<Typography variant="body2">{n.content}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{n.createdByName || 'User'} · {fmtDateTime(n.createdAt)}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <LinkDepositDialog open={linkOpen} clientId={client.client_id} onClose={() => setLinkOpen(false)} onLinked={load} />

      <Dialog open={noteOpen} onClose={savingNote ? undefined : () => setNoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            label="Note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteOpen(false)} disabled={savingNote}>Cancel</Button>
          <Button variant="contained" onClick={addNote} disabled={savingNote}>{savingNote ? 'Saving…' : 'Add Note'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
